import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';

const isE2EMode = import.meta.env.VITE_E2E_MODE === 'true';

export const userPhotoKeys = {
  photo: (userId: string | undefined, tenantId: string | undefined) =>
    ['user-photo', userId?.toLowerCase(), tenantId?.toLowerCase()] as const,
};

/** Read a Blob as a base64 data URL so it can be cached as a plain string
 * (no object-URL lifecycle / revocation to manage). */
const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

/**
 * Fetches a Microsoft Graph profile photo and returns it as a data URL, or
 * `undefined` when there's no photo / no access / any failure.
 *
 * Pass a `userId` to fetch any user's photo; omit it to fetch the signed-in
 * user's photo (the original behaviour). Uses `/users/{id}/photo/$value`,
 * which works with the `User.ReadBasic.All` scope the app already requests.
 * Callers should fall back to initials when this is `undefined` — Fluent's
 * <Avatar> does this automatically when no `image` is set.
 *
 * Failure modes that resolve to "no photo" (not an error):
 *  - 404: the user has not set a profile photo
 *  - 403: Graph access not consented for this app/tenant
 *  - network / token failures
 */
export const useUserPhoto = (userId?: string): string | undefined => {
  const { isAuthenticated, account, getGraphToken } = useAuth();
  // Default to the signed-in user when no id is provided — preserves the
  // hook's original ergonomics for the header avatar.
  const effectiveUserId = userId ?? account?.localAccountId;
  const tenantId = account?.tenantId;

  const { data } = useQuery({
    queryKey: userPhotoKeys.photo(effectiveUserId, tenantId),
    // Skip in E2E/mock so Playwright snapshots stay deterministic and offline.
    enabled: isAuthenticated && !!effectiveUserId && !isE2EMode,
    staleTime: 1000 * 60 * 60, // 1 hour — photos rarely change within a session
    gcTime: 1000 * 60 * 60 * 24,
    retry: false,
    queryFn: async (): Promise<string | null> => {
      try {
        const token = await getGraphToken();
        if (!token) return null;

        // Use the default photo endpoint rather than a fixed size: a specific
        // rendition (e.g. 48x48) can 404 even when the user has a photo if that
        // size hasn't been generated for their mailbox. The default returns the
        // available image, which the avatar downscales.
        const response = await fetch(
          `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(effectiveUserId ?? '')}/photo/$value`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        // 404 = no photo set, 403 = no Graph consent — both are "no photo".
        if (!response.ok) return null;

        const blob = await response.blob();
        if (blob.size === 0) return null;
        return await blobToDataUrl(blob);
      } catch {
        // Network/token/decoding failure — fall back to initials.
        return null;
      }
    },
  });

  return data ?? undefined;
};
